import { useState, useEffect } from 'react';
import { notification, Card, Button, Space, Typography, List, Tag, Avatar, Alert, Divider } from 'antd';
import { CheckCircleFilled, ExclamationCircleOutlined, UserOutlined, ExperimentOutlined } from '@ant-design/icons';
import Modal from '../Modal/Modal';
import { t } from '../../i18n/i18n';
import { TestGroupTypes } from '../../constants/enums';
import { postRestartTestgroup, getRestartScreeningData } from '../../API/services/screeningService';
import Spinner from '../spinner/Spinner';
import HorizontalLoader from '../HorizontalLoader/HorizontalLoader';
import './RestartTestgroupModal.scss';

const { Title, Text } = Typography;

const RestartTestgroupModal = ({
  show,
  onClose,
  screeningInstanceId,
  candidateName,
  candidateEmail,
  selectedSearch,
  screeningInstanceIds = [],
  candidatesInfo = [],
  isBatchMode = false,
}) => {
  const [selectedTestgroups, setSelectedTestgroups] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [evaluationTestgroups, setEvaluationTestgroups] = useState([]);
  const [loadingTestgroups, setLoadingTestgroups] = useState(false);

  const fetchEvaluationTestgroups = async () => {
    if (!selectedSearch || !selectedSearch.stages || !selectedSearch.stages[0]) {
      return;
    }

    try {
      setLoadingTestgroups(true);
      const stage = selectedSearch.stages[0];
      const screeningData = await getRestartScreeningData({
        levelId: stage.levelId,
        companyId: stage.companyId,
      });

      setEvaluationTestgroups(screeningData.testGroups || []);
    } catch (error) {
      console.error('Error fetching evaluation testgroups:', error);
      setEvaluationTestgroups([]);
    } finally {
      setLoadingTestgroups(false);
    }
  };

  useEffect(() => {
    if (show) {
      setSelectedTestgroups([]);
      setShowConfirmModal(false);
      fetchEvaluationTestgroups();
    }
  }, [show]);

  const handleSelectTestgroup = (testgroupId) => {
    if (selectedTestgroups.includes(testgroupId)) {
      setSelectedTestgroups(selectedTestgroups.filter(id => id !== testgroupId));
    } else {
      setSelectedTestgroups([...selectedTestgroups, testgroupId]);
    }
  };

  const handleShowConfirmModal = () => {
    if (selectedTestgroups.length > 0) {
      setShowConfirmModal(true);
    }
  };

  const handleBackToSelection = () => {
    setShowConfirmModal(false);
  };

  const handleConfirmRestart = async () => {
    try {
      setLoading(true);

      const instanceIds = isBatchMode 
        ? screeningInstanceIds 
        : [screeningInstanceId];
      
      const dataToPost = {
        testGroupIds: selectedTestgroups,
        instanceIds: instanceIds,
      };

      await postRestartTestgroup(dataToPost);

      notification.open({
        message: t('POST_RESET_TESTGROUP_NOTIFICATION'),
        type: 'info',
      });

      setShowConfirmModal(false);
      onClose();
    } catch (error) {
      notification.open({
        message: t('ERROR_RESET_TESTGROUP_NOTIFICATION') || 'Error al reiniciar testgroup',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowConfirmModal(false);
    onClose();
  };

  const renderModalContent = () => {
    if (showConfirmModal) {
      return (
        <div className="reset-testgroup-modal-container confirmation-step modern-modal modal-content-enter">
          <div className="modal-content-wrapper">
            <Alert
              message={t('RESET_TESTGROUP_STEP_4_MODAL_WARNING')}
              type="error"
              icon={<ExclamationCircleOutlined />}
              className="mb-4"
              showIcon
            />

            <Card className="confirmation-card" bordered={false}>
              <Space direction="vertical" size="large" className="w-100">
                <div>
                  <Title level={5} className="mb-3">
                    <ExperimentOutlined className="mr-2" />
                    {t('RESET_TESTGROUP_STEP_4_MODAL_FIRST_ITEM')}
                  </Title>
                  <List
                    size="small"
                    dataSource={evaluationTestgroups.filter((item) => selectedTestgroups.includes(item.id))}
                    renderItem={(item) => (
                      <List.Item className="py-2">
                        <Tag color="blue" className="testgroup-tag">
                          {item.type !== TestGroupTypes.PRE_FILTER
                            ? item.name
                            : t('TESTGROUP_PRE_FILTER_TITLE_NAME')}
                        </Tag>
                      </List.Item>
                    )}
                  />
                </div>

                <Divider className="my-2" />

                <div>
                  <Title level={5} className="mb-3">
                    <UserOutlined className="mr-2" />
                    {t('CANDIDATE_LABEL')}
                  </Title>
                  {isBatchMode ? (
                    <Card size="small" className="candidate-info-card">
                      <Space direction="vertical" className="w-100">
                        <Space>
                          <Avatar icon={<UserOutlined />} size="small" />
                          <Text strong>{candidatesInfo.length} {t('CANDIDATES_SELECTED') || 'candidatos'}</Text>
                        </Space>
                        <List
                          size="small"
                          dataSource={candidatesInfo}
                          renderItem={(candidate) => (
                            <List.Item className="candidate-confirmation-item">
                              <Space>
                                <Text>•</Text>
                                <Text strong>{candidate.name || candidate.email}</Text>
                                {candidate.name && (
                                  <Text type="secondary" className="text-xs">({candidate.email})</Text>
                                )}
                              </Space>
                            </List.Item>
                          )}
                        />
                      </Space>
                    </Card>
                  ) : (
                    <Card size="small" className="candidate-info-card">
                      <Space>
                        <Avatar icon={<UserOutlined />} size="small" />
                        <div>
                          <Text strong>{candidateName}</Text>
                          <br />
                          <Text type="secondary" className="text-xs">{candidateEmail}</Text>
                        </div>
                      </Space>
                    </Card>
                  )}
                </div>
              </Space>
            </Card>
          </div>

          <div className="modal-footer">
            {loading ? (
              <div className="processing-container">
                <HorizontalLoader className="processing-loader" />
              </div>
            ) : (
              <Space>
                <Button onClick={handleBackToSelection} className="cancel-btn">
                  {t('RESET_TESTGROUP_BACK_BUTTON')}
                </Button>
                <Button
                  type="primary"
                  danger
                  onClick={handleConfirmRestart}
                  className="confirm-btn"
                >
                  {t('RESET_TESTGROUP_STEP_4_MODAL_ACCEPT_BUTTON')}
                </Button>
              </Space>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="reset-testgroup-modal-container modern-modal modal-content-enter">
        <div className="modal-content-wrapper">
          <div className="modal-header">
            <Title level={4} className="mb-2">
              <ExperimentOutlined className="mr-2" style={{ color: '#4987BE' }} />
              {t('RESTART_TESTGROUP_MODAL_SELECT_TITLE')}
            </Title>

            <Card size="small" className="candidate-summary-card">
              {isBatchMode ? (
                <Space direction="vertical" className="w-100">
                  <Space>
                    <Avatar icon={<UserOutlined />} size="small" style={{ backgroundColor: '#4987BE' }} />
                    <Text strong>{candidatesInfo.length} {t('CANDIDATES_SELECTED') || 'candidatos seleccionados'}</Text> {/* TODO: Verificar traducción existente */}
                  </Space>
                  <div className="batch-candidates-preview">
                    {candidatesInfo.slice(0, 3).map((candidate, index) => (
                      <Text key={index} type="secondary" className="candidate-email-batch">
                        • {candidate.email}
                      </Text>
                    ))}
                    {candidatesInfo.length > 3 && (
                      <Text type="secondary">
                        {t('AND_MORE_CANDIDATES').replace(`{othersCandidates}`, candidatesInfo.length - 3)}
                      </Text>
                    )}
                  </div>
                </Space>
              ) : (
                <Space>
                  <Avatar icon={<UserOutlined />} size="small" style={{ backgroundColor: '#4987BE' }} />
                  <div>
                    <Text strong className="candidate-name">{candidateName}</Text>
                    <br />
                    <Text type="secondary" className="candidate-email">{candidateEmail}</Text>
                  </div>
                </Space>
              )}
            </Card>
          </div>

          <div className="testgroups-selection-area">
            {loadingTestgroups ? (
              <div className="loading-container">
                <Spinner show />
              </div>
            ) : (
              <List
                className="testgroups-list"
                dataSource={evaluationTestgroups?.filter((item) => item.type !== TestGroupTypes.PRE_FILTER)}
                renderItem={(testgroup) => (
                  <List.Item className="testgroup-item">
                    <Card
                      hoverable
                      onClick={() => handleSelectTestgroup(testgroup.id)}
                      className={`testgroup-card ${selectedTestgroups.includes(testgroup.id) ? 'selected-card' : ''}`}
                      size="small"
                    >
                      <div className="testgroup-content">
                        <Space className="w-100 justify-content-between">
                          <div className="testgroup-info">
                            <Text strong className="testgroup-name">{testgroup.name}</Text>
                          </div>
                          {selectedTestgroups.includes(testgroup.id) && (
                            <CheckCircleFilled className="check-icon" style={{ color: '#52c41a', fontSize: '18px' }} />
                          )}
                        </Space>
                      </div>
                    </Card>
                  </List.Item>
                )}
              />
            )}
          </div>
        </div>

        <div className="modal-footer">
          <Space>
            <Button onClick={handleClose} className="cancel-btn">
              {t('RESET_TESTGROUP_BACK_BUTTON')}
            </Button>
            <Button
              type="primary"
              onClick={handleShowConfirmModal}
              disabled={!selectedTestgroups.length}
              className="confirm-btn"
            >
              {t('RESET_TESTGROUP_STEP_4_MODAL_ACCEPT_BUTTON')} ({selectedTestgroups.length})
            </Button>
          </Space>
        </div>
      </div>
    );
  };

  return (
    <Modal
      show={show}
      relativeCloseButton
      className="rounded px-4 pt-2 pb-3 w-50"
      closeBtnClassname="text-blue-principal font-weight-bold"
      setShow={handleClose}
    >
      {renderModalContent()}
    </Modal>
  );
};

export default RestartTestgroupModal;